import { Component, inject, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';

import { UsersService } from '../../services/users.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  standalone: false, // eslint-disable-line @angular-eslint/prefer-standalone
})
export class LoginComponent implements OnInit {
  public isSubmitting = false;

  public email = '';
  public password = '';
  public rememberMe = false;

  private usersService: UsersService;
  private router: Router;
  private toastr: ToastrService;

  constructor() {
    this.usersService = inject(UsersService);
    this.router = inject(Router);
    this.toastr = inject(ToastrService);
  }

  ngOnInit(): void {
    this.usersService.getConnectedUser().subscribe((user) => {
      if (user != null) {
        this.router.navigate(['/home']);
      }
    });
  }

  /**
   * Sign in with Google or Facebook
   * @param f
   * @param provider
   */
  public signInWithProvider(f: NgForm, provider: string): void {
    this.isSubmitting = true;
    f.form.disable();
    // eslint-disable-next-line promise/catch-or-return
    this.usersService
      .signinWithProvider(provider)
      .then(async (credential) => {
        const user = credential.user;
        // eslint-disable-next-line promise/always-return
        if (user) {
          if (user.phoneNumber) {
            const isAdmin = await this.usersService.isUserAdmin(user);
            if (isAdmin) {
              this.usersService.updateConnectedUser(user);
              this.router.navigate(['/home']);
            } else {
              this.toastr.error('User is not an admin');
            }
          } else {
            this.toastr.error('User has no valid phone number');
          }
        }
      })
      .catch((error) => {
        // The provider's credential:
        alert(error.message);
        // eslint-disable-next-line promise/catch-or-return, promise/no-nesting
        this.usersService.signOut().then(null, null);
      })
      .finally(() => {
        f.form.enable();
        this.isSubmitting = false;
      });
  }

  public login(f: NgForm): void {
    if (f.valid) {
      this.isSubmitting = true;
      f.form.disable();

      this.usersService
        .signInWithEmail(this.email, this.password)
        .pipe(
          finalize(() => {
            this.isSubmitting = false;
            f.form.enable();
          })
        )
        // eslint-disable-next-line sonarjs/deprecation
        .subscribe(
          (credential) => {
            const user = credential.user;
            if (user?.phoneNumber) {
              this.usersService.updateConnectedUser(user);
              this.router.navigate(['/home']);
            } else {
              this.toastr.error('User has no valid phone number');
            }
          },
          (error) => {
            const errorCode = error.code;
            if (errorCode === 'auth/wrong-password') {
              this.toastr.error('Invalid password');
            }
            if (errorCode === 'auth/invalid-email') {
              this.toastr.error('Invalid email');
            }
            if (errorCode === 'auth/user-not-found') {
              this.toastr.error(
                "Sorry, we couldn't find your account. Please try again with a different email"
              );
              console.error(error.message);
            }
            if (errorCode === 'auth/user-disabled') {
              this.toastr.error('User is disabled');
              console.error(error.message);
            }
          }
        );
    }
  }
}
